"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { SheetHeader } from "@/components/tools/printables/sheet-header";
import { SystemArt } from "@/components/tools/printables/staff-page";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";
import {
  type Clef,
  layoutStaffPage,
  MAX_SPACE,
  MIN_SPACE,
  systemHeight,
} from "@/lib/tool-kit/printables/staff-paper";

/** English strings. A translated content file overrides these through `ui`. */
export const STAFF_PAPER_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  clefLabel: "Staff",
  clefs: {
    treble: "Treble clef",
    bass: "Bass clef",
    grand: "Grand staff (treble and bass, braced)",
    none: "Blank staff, no clef",
  },
  sizeLabel: "Staff size (distance between lines)",
  sizes: {
    "1.75": "1.75 mm — 7 mm staff, engraved size",
    "2": "2 mm — 8 mm staff, standard manuscript",
    "2.5": "2.5 mm — 10 mm staff, comfortable handwriting",
    "3.5": "3.5 mm — 14 mm staff, young players",
    "5": "5 mm — 20 mm staff, beginners",
    "7": "7 mm — 28 mm staff, early learners",
    custom: "Custom size",
  },
  customLabel: "Staff space in millimetres",
  customHint: "From {min} to {max} mm. A staff is four spaces tall.",
  badSize: "Enter a staff space from {min} to {max} mm.",
  systemsLabel: "Staves per page",
  systemsFit: "As many as fit",
  systemsFitNotice: "Only {max} fit on a page at this size.",
  measuresLabel: "Barlines (measures per staff)",
  measuresNone: "None",
  showClef: "Draw the clef at the start of each staff",
  header: "Title and composer lines on page 1",
  titleLabel: "Title (optional)",
  titleHint:
    "Printed above the first staff. Leave it empty to get a Title line to write on.",
  titleLine: "Title",
  composerLine: "Composer",
  pagesLabel: "Pages",
  weightLabel: "Line weight",
  weights: { thin: "Thin", standard: "Standard", bold: "Bold" },
  summary: "Staff height {height} mm — {space} mm between lines.",
  previewLabel: "Staff paper preview",
} as const;

const SIZE_KEYS = ["1.75", "2", "2.5", "3.5", "5", "7", "custom"] as const;
const WEIGHT = { thin: 0.75, standard: 1, bold: 1.45 } as const;
const MEASURE_CHOICES = ["0", "1", "2", "3", "4", "5", "6", "8"];
const SYSTEM_CHOICES = [
  "0",
  ...Array.from({ length: 16 }, (_, i) => String(i + 1)),
];
const PAGE_CHOICES = Array.from({ length: 10 }, (_, i) => String(i + 1));

type SizeKey = (typeof SIZE_KEYS)[number];
type WeightKey = keyof typeof WEIGHT;

function headerHeight(hasTitle: boolean): number {
  return hasTitle ? 24 : 13;
}

export default function StaffPaperGeneratorWidget() {
  const strings = useToolUi(STAFF_PAPER_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [clef, setClef] = useState<Clef>("treble");
  const [sizeKey, setSizeKey] = useState<SizeKey>("2");
  const [custom, setCustom] = useState("2.2");
  const [systems, setSystems] = useState("0");
  const [measures, setMeasures] = useState("0");
  const [showClef, setShowClef] = useState(true);
  const [header, setHeader] = useState(false);
  const [title, setTitle] = useState("");
  const [pages, setPages] = useState("1");
  const [weight, setWeight] = useState<WeightKey>("standard");
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");

  const customValue = parseNumberInput(custom);
  const customOk =
    customValue !== null &&
    customValue >= MIN_SPACE &&
    customValue <= MAX_SPACE;
  const chosen = sizeKey === "custom" ? customValue : Number(sizeKey);
  const space = customOk || sizeKey !== "custom" ? (chosen ?? 2) : 2;
  const geometry = pageGeometry(paper, orientation);
  const hasTitle = title.trim().length > 0;
  const options = {
    clef,
    space,
    systems: Number(systems),
    measures: Number(measures),
    showClef,
    header,
  };
  const first = layoutStaffPage(
    geometry,
    options,
    header ? headerHeight(hasTitle) : 0
  );
  const rest = layoutStaffPage(geometry, options, 0);
  const fields = hasTitle
    ? [{ label: strings.composerLine, share: 1 }]
    : [
        { label: strings.titleLine, share: 3 },
        { label: strings.composerLine, share: 2 },
      ];
  const sheets = Array.from({ length: Number(pages) }, (_, index) => {
    const page = index === 0 ? first : rest;
    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
      <g key={`staff-${index}`}>
        {index === 0 && header ? (
          <SheetHeader
            fields={fields}
            geometry={geometry}
            measure={measure}
            title={title.trim()}
          />
        ) : null}
        {page.systems.map((system) => (
          <SystemArt
            key={system.y1}
            space={space}
            system={system}
            weight={WEIGHT[weight]}
          />
        ))}
      </g>
    );
  });
  const staffHeight = systemHeight("none", space);

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="sp-clef"
            label={strings.clefLabel}
            onChange={setClef}
            options={(["treble", "bass", "grand", "none"] as const).map(
              (value) => ({
                value,
                label: strings.clefs[value],
              })
            )}
            value={clef}
          />
          <SelectField
            id="sp-size"
            label={strings.sizeLabel}
            onChange={setSizeKey}
            options={SIZE_KEYS.map((value) => ({
              value,
              label: strings.sizes[value],
            }))}
            value={sizeKey}
          />
          {sizeKey === "custom" ? (
            <TextField
              hint={t(strings.customHint, { min: MIN_SPACE, max: MAX_SPACE })}
              id="sp-custom"
              label={strings.customLabel}
              numeric
              onChange={setCustom}
              value={custom}
            />
          ) : null}
          <SelectField
            id="sp-systems"
            label={strings.systemsLabel}
            onChange={setSystems}
            options={SYSTEM_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.systemsFit : value,
            }))}
            value={systems}
          />
          <SelectField
            id="sp-measures"
            label={strings.measuresLabel}
            onChange={setMeasures}
            options={MEASURE_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.measuresNone : value,
            }))}
            value={measures}
          />
          <SelectField
            id="sp-weight"
            label={strings.weightLabel}
            onChange={setWeight}
            options={(["thin", "standard", "bold"] as const).map((value) => ({
              value,
              label: strings.weights[value],
            }))}
            value={weight}
          />
          <SelectField
            id="sp-pages"
            label={strings.pagesLabel}
            onChange={setPages}
            options={PAGE_CHOICES.map((value) => ({ value, label: value }))}
            value={pages}
          />
          <PaperControls
            idPrefix="sp"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {clef === "none" ? null : (
            <Checkbox
              checked={showClef}
              label={strings.showClef}
              onChange={setShowClef}
            />
          )}
          <Checkbox
            checked={header}
            label={strings.header}
            onChange={setHeader}
          />
        </div>
        {header ? (
          <TextField
            hint={strings.titleHint}
            id="sp-title"
            label={strings.titleLabel}
            onChange={setTitle}
            value={title}
          />
        ) : null}
        {sizeKey === "custom" && !customOk ? (
          <ErrorNotice>
            {t(strings.badSize, { min: MIN_SPACE, max: MAX_SPACE })}
          </ErrorNotice>
        ) : null}
        {first.fits ? null : (
          <Notice>{t(strings.systemsFitNotice, { max: first.max })}</Notice>
        )}
        <PrintBar
          fileName={`staff-paper-${clef}-${paper}`}
          pageTotal={sheets.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
        <p aria-live="polite" className="text-muted-foreground text-xs">
          {t(strings.summary, {
            height: Math.round(staffHeight * 100) / 100,
            space,
          })}
        </p>
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={sheets}
        svgRef={svgRef}
      />
    </div>
  );
}
